import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Lock, ShieldCheck, Zap, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/dialog";
import { CountdownBoxes } from "@/components/CountdownTimer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CITY_OPTIONS } from "@/components/InquiryForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "soldesk_offer_seen";

export default function OfferPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "" });
  const [loading, setLoading] = useState(false);
  const typedRef = useRef(false);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("soldesk:open-offer", openHandler);
    let t;
    if (!sessionStorage.getItem(SEEN_KEY)) {
      t = setTimeout(() => {
        if (!sessionStorage.getItem(SEEN_KEY)) setOpen(true);
      }, 4000);
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener("soldesk:open-offer", openHandler);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    // Auto-close after 20s if the visitor hasn't started typing
    const t = setTimeout(() => {
      if (!typedRef.current) close(false);
    }, 20000);
    return () => clearTimeout(t);
  }, [open]);

  const close = (v) => {
    setOpen(v);
    if (!v) sessionStorage.setItem(SEEN_KEY, "1");
  };

  const set = (k) => (e) => {
    typedRef.current = true;
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.city) {
      toast.error("Please select your city");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        phone: form.phone,
        business_type: "Festive Offer Claim",
        city: form.city,
        duration: "12",
        message: `Festive Offer: 12 months pay for 11 + FREE GST registration.${form.email ? ` Email: ${form.email}` : ""}`,
        source: "festive-offer",
      });
      toast.success("Offer claimed! Our team will call you to set up your virtual office.");
      sessionStorage.setItem(SEEN_KEY, "1");
      setOpen(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-3xl p-0 overflow-y-auto sm:max-w-3xl border-0 max-h-[calc(100dvh-2rem)] [&>button:last-of-type]:hidden" data-testid="offer-popup">
        <DialogTitle className="sr-only">Festive offer — get 12 months, pay for 11</DialogTitle>
        <DialogDescription className="sr-only">Claim the festive offer on annual virtual office plans.</DialogDescription>
        <DialogClose asChild>
          <button data-testid="offer-popup-close" aria-label="Close offer popup"
            className="z-[60] w-11 h-11 rounded-full bg-white text-slate-900 shadow-lg flex items-center justify-center transition-transform duration-200 hover:scale-110 active:scale-95 max-sm:fixed max-sm:top-4 max-sm:right-4 sm:absolute sm:right-3 sm:top-3 sm:z-20">
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </DialogClose>
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Offer panel */}
          <div className="bg-slate-900 p-8 sm:p-10 flex flex-col justify-between min-h-[280px]">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3.5 py-1 text-xs font-bold tracking-[0.15em] uppercase text-white/90">
                <Sparkles className="w-3.5 h-3.5 text-primary" /> Festive Special
              </div>
              <h3 className="mt-5 font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
                Get 12 months.<br />Pay for just <span className="text-primary">11!</span>
              </h3>
              <p className="mt-4 text-sm text-white/70 leading-relaxed">
                Buy a virtual office for a year and pay for just 11 months — plus get{" "}
                <span className="text-white font-semibold">GST registration absolutely FREE</span>.
                Valid on all plans, in every city, till Diwali.
              </p>
              <div className="mt-6">
                <CountdownBoxes dark />
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2">
              {[
                { icon: Lock, text: "Price lock" },
                { icon: ShieldCheck, text: "GST-ready docs" },
                { icon: Zap, text: "24-48 hr setup" },
              ].map(({ icon: Icon, text }) => (
                <span key={text} className="flex items-center gap-1.5 text-xs font-medium text-white/80">
                  <Icon className="w-3.5 h-3.5 text-primary" /> {text}
                </span>
              ))}
            </div>
          </div>

          {/* Claim form */}
          <div className="p-8 sm:p-10 bg-white">
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-primary">Claim Festive Offer</p>
            <h3 className="mt-2 font-display text-2xl font-semibold text-slate-900">Lock your price</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">A Solve Desk expert will get in touch to set up your virtual office.</p>
            <form onSubmit={submit} className="mt-6 space-y-3.5" data-testid="offer-form">
              <Input data-testid="offer-name-input" placeholder="Full Name" className="h-12 rounded-xl bg-slate-50 border-slate-200"
                value={form.name} onChange={set("name")} required />
              <Input data-testid="offer-phone-input" placeholder="Mobile Number" className="h-12 rounded-xl bg-slate-50 border-slate-200"
                value={form.phone} onChange={set("phone")} required />
              <Input data-testid="offer-email-input" type="email" placeholder="Work / Personal Email (optional)" className="h-12 rounded-xl bg-slate-50 border-slate-200"
                value={form.email} onChange={set("email")} />
              <Select value={form.city} onValueChange={set("city")}>
                <SelectTrigger data-testid="offer-city-select" className="h-12 rounded-xl bg-slate-50 border-slate-200">
                  <SelectValue placeholder="Preferred City" />
                </SelectTrigger>
                <SelectContent>
                  {CITY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit" disabled={loading} data-testid="offer-claim-button"
                className="w-full h-13 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-transform duration-200 hover:-translate-y-0.5">
                {loading ? "Claiming..." : "Claim +1 Free Month"} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
              <p className="text-center text-xs text-muted-foreground">Fast response · Zero spam guarantee</p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
